import { Suspense } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function HomeAutomationPage() {
  useEffect(() => {
    document.title = 'Home Automation Systems Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Advanced home automation systems in Watford. Control your home with smart technology. NICEIC approved installers. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/home-automation",
      "areaServed": "Watford",
      "serviceType": "Home Automation Systems",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
                <i className="ri-map-pin-line text-amber-500"></i>
                <span className="text-sm font-medium text-gray-700">Serving Watford & Surrounding Areas</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Home Automation Systems <span className="text-amber-500">in Watford</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Complete home automation solutions that integrate all your smart devices into one seamless system. Professional installation and programming throughout Watford and Rickmansworth.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                  Call 020 3198 1818
                </a>
                <button onClick={scrollToContact} className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                  Get Free Quote
                </button>
              </div>
              
              <div className="flex flex-wrap gap-6 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">NICEIC Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">15 Years Experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-settings-3-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">Custom Programming</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Automation Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Can Automate</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Integrate and automate every aspect of your home
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-sun-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Lighting Scenes</h3>
                <p className="text-gray-600 leading-relaxed">
                  Create custom lighting scenes for different times of day, activities, or moods with one tap.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-temp-cold-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Climate Control</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automated heating and cooling that adapts to your schedule and weather conditions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-door-lock-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Security Integration</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automated locks, cameras, and alarms that work together to protect your home.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-window-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Blinds & Curtains</h3>
                <p className="text-gray-600 leading-relaxed">
                  Motorized window treatments that open and close based on time, sunlight, or temperature.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-music-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Room Audio</h3>
                <p className="text-gray-600 leading-relaxed">
                  Synchronized music throughout your home or different audio in each room.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-water-flash-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Garden & Irrigation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automated watering systems, outdoor lighting, and garden feature control.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Automation Scenarios */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Automation Scenarios</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Pre-programmed routines that make your life easier
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-sun-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Good Morning</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Lights gradually brighten, heating adjusts, blinds open, and your favorite music starts playing — all automatically at your wake-up time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Arriving Home</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Garage opens, lights turn on, heating activates, and security system disarms as you approach your home.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-door-lock-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Leaving Home</h3>
                    <p className="text-gray-600 leading-relaxed">
                      All lights turn off, heating reduces, doors lock, and security system arms with a single button press.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-moon-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Good Night</h3>
                    <p className="text-gray-600 leading-relaxed">
                      All lights turn off, doors lock, heating lowers, and security cameras activate for the night.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tv-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Movie Time</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Lights dim, blinds close, TV turns on, and surround sound activates — perfect cinema experience.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-plane-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Holiday Mode</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Lights turn on/off randomly, heating maintains minimum temperature, and you receive security alerts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Home Automation?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transform your daily routine with intelligent automation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-magic-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Effortless Living</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your home anticipates your needs and adjusts automatically without any manual intervention.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-leaf-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Energy Efficiency</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Intelligent automation reduces waste by only using energy when and where it's needed.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-check-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Enhanced Security</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Integrated security systems provide comprehensive protection and instant alerts.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-home-smile-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Increased Property Value</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Professional home automation systems are a premium feature that adds significant value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Beyond Electrical?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Expert home automation specialists in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-code-s-slash-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Custom Programming</h3>
                <p className="text-gray-600 leading-relaxed">
                  Tailored automation routines designed specifically for your lifestyle and preferences.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-award-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">15 Years Experience</h3>
                <p className="text-gray-600 leading-relaxed">
                  Extensive experience in electrical systems and smart home technology integration.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Local support team available for adjustments, upgrades, and troubleshooting.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Areas Covered */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Areas We Cover</h2>
              <div className="flex flex-wrap justify-center gap-4 text-gray-700">
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Watford</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Rickmansworth</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Bushey</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Abbots Langley</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Croxley Green</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Chorleywood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Northwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Radlett</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Borehamwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">St Albans</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Hemel Hempstead</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Harrow</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact" className="py-20 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Automate Your Home?</h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get a free consultation and custom automation plan for your home in Watford
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}